/**
 * Discount Popup Component
 * Handles popup display, sessionStorage, animations, and coupon code copying
 */

class DiscountPopup {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
    this.popup = container;
    this.overlay = container.querySelector('.discount-popup__overlay');
    this.closeBtn = container.querySelector('.discount-popup__close');
    this.copyBtn = container.querySelector('.discount-popup__copy-btn');
    this.couponCode = container.querySelector('[id^="coupon-code-"]');
    this.delay = parseInt(container.dataset.delay || '3') * 1000 || 3000;
    this.coupon = container.dataset.couponCode || 'WELCOME10';
    this.storageKey = 'discount_popup_shown';

    this.init();
  }

  init() {
    console.log('Discount Popup: Initializing...', {
      pathname: window.location.pathname,
      isHomepage: this.isHomepage(),
      sessionStorage: sessionStorage.getItem(this.storageKey),
      delay: this.delay
    });

    // Check if popup was already shown in this session
    if (sessionStorage.getItem(this.storageKey) === 'true') {
      console.log('Discount Popup: Already shown in this session');
      return;
    }

    // Check if we're on homepage
    if (!this.isHomepage()) {
      console.log('Discount Popup: Not on homepage, skipping');
      return;
    }

    console.log('Discount Popup: Will show after', this.delay, 'ms');

    // Wait for delay, then show popup
    setTimeout(() => {
      console.log('Discount Popup: Showing popup now hello world ');
      this.show();
    }, this.delay);

    // Setup event listeners
    this.setupEventListeners();
  }

  isHomepage() {
    const path = window.location.pathname;
    const isHome = path === '/' || path === '/index' || path === '/index.html' || path.match(/^\/\?/);
    return isHome;
  }

  setupEventListeners() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Copy coupon code
    if (this.copyBtn) {
      this.copyBtn.addEventListener('click', () => this.copyCouponCode());
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.style.display !== 'none') {
        this.close();
      }
    });
  }

  show() {
    this.popup.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Trigger animation after a small delay to ensure display is set
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.popup.classList.add('discount-popup--visible');
      });
    });
  }

  close() {
    this.popup.classList.remove('discount-popup--visible');
    document.body.style.overflow = '';

    // Hide after animation
    setTimeout(() => {
      this.popup.style.display = 'none';
      // Mark as shown in sessionStorage
      sessionStorage.setItem(this.storageKey, 'true');
    }, 300);
  }

  async copyCouponCode() {
    try {
      await navigator.clipboard.writeText(this.coupon);
      
      // Update button text
      const copyText = this.copyBtn.querySelector('.discount-popup__copy-text');
      if (copyText) {
        const originalText = copyText.textContent;
        copyText.textContent = 'Copied!';
        this.copyBtn.classList.add('discount-popup__copy-btn--copied');

        // Reset after 2 seconds
        setTimeout(() => {
          copyText.textContent = originalText;
          this.copyBtn.classList.remove('discount-popup__copy-btn--copied');
        }, 2000);
      }
    } catch (err) {
      console.warn('Failed to copy coupon code:', err);
      // Fallback: select text
      if (this.couponCode) {
        try {
          const range = document.createRange();
          range.selectNodeContents(this.couponCode);
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } catch (e) {
          console.warn('Could not select text:', e);
        }
      }
    }
  }
}

// Initialize popup when DOM is ready
function initDiscountPopups() {
  const popupContainers = document.querySelectorAll('[id^="discount-popup-"]');
  
  if (popupContainers.length === 0) {
    console.warn('Discount Popup: No popup containers found in DOM');
    console.warn('Discount Popup: Make sure the section is added to homepage template');
    return;
  }
  
  console.log('Discount Popup: Found', popupContainers.length, 'popup container(s)');
  
  popupContainers.forEach((container) => {
    if (!(container instanceof HTMLElement)) return;
    if (!container.dataset.initialized) {
      container.dataset.initialized = 'true';
      console.log('Discount Popup: Initializing popup', container.id);
      new DiscountPopup(container);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDiscountPopups);
} else {
  initDiscountPopups();
}

// Also try after a short delay in case sections load late
setTimeout(initDiscountPopups, 1000);

// Also check for dynamically added popups
const popupObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1 && node instanceof HTMLElement && node.id && node.id.startsWith('discount-popup-')) {
        if (!node.dataset.initialized) {
          node.dataset.initialized = 'true';
          console.log('Discount Popup: Found dynamically added popup', node.id);
          new DiscountPopup(node);
        }
      }
    });
  });
});

if (document.body) {
  popupObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
}

// Expose for manual testing
// @ts-ignore
window.testDiscountPopup = function() {
  sessionStorage.removeItem('discount_popup_shown');
  const popup = document.querySelector('[id^="discount-popup-"]');
  if (popup && popup instanceof HTMLElement) {
    popup.style.display = 'block';
    document.body.style.overflow = 'hidden';
    popup.classList.add('discount-popup--visible');
    console.log('Discount Popup: Manually shown for testing');
    return true;
  } else {
    console.error('Discount Popup: Popup element not found in DOM');
    console.log('Discount Popup: Available elements:', document.querySelectorAll('[id*="discount"]'));
    return false;
  }
};

